import { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { WeeklyCalendar } from './WeeklyCalendar';
import { getWeekStart } from '@/utils/dateUtils';
import { useMealPlanByWeek } from '@/hooks';
import { CalendarDays, X } from 'lucide-react';
import { Recipe } from '@/types/api';

interface MealPlannerModalProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (data: { dateISO: string; service: 'comida' | 'cena' }) => void;
  recipe: Recipe;
}

export function MealPlannerModal({ open, onClose, onConfirm, recipe }: MealPlannerModalProps) {

  const today = useMemo(() => new Date(), []);
  const [weekStart, setWeekStart] = useState<Date>(() => getWeekStart(today));
  
  // Get meal plan for current week
  const weekStartISO = weekStart.toISOString().split('T')[0]; // YYYY-MM-DD format
  const { data: mealPlan, isLoading } = useMealPlanByWeek(weekStartISO);

  const handleSlotSelect = (day: Date, service: 'comida' | 'cena') => {
    const dateISO = day.toISOString();
    onConfirm({ dateISO, service });
  };

  const navigateWeek = (direction: 'prev' | 'next') => {
    setWeekStart(prev => {
      const newDate = new Date(prev);
      newDate.setDate(newDate.getDate() + (direction === 'next' ? 7 : -7));
      return newDate;
    });
  };

  if (!open) return null;

  const modal = (
    <div
      className="fixed inset-0 z-[9999] flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-sm p-2 sm:p-4"
      role="dialog"
      aria-modal="true"
      onClick={onClose}
    >
      <Card
        className="w-full max-w-6xl max-h-[95vh] sm:max-h-[90vh] overflow-hidden shadow-xl 
                   rounded-t-2xl sm:rounded-2xl border-0 
                   animate-in slide-in-from-bottom-2 sm:zoom-in-95 
                   duration-300 ease-out"
        onClick={(e) => e.stopPropagation()}
      >
        <CardHeader className="pb-3 px-4 sm:px-6">
          {/* Indicador móvil */}
          <div className="flex justify-center sm:hidden mb-2">
            <div className="w-10 h-1 bg-gray-300 rounded-full"></div>
          </div>
          
          {/* Header con título y botón cerrar */}
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-start gap-3 min-w-0 flex-1">
              <div className="flex-shrink-0 mt-0.5">
                <CalendarDays className="h-5 w-5 text-primary" />
              </div>
              <div className="min-w-0 flex-1">
                <CardTitle className="text-lg sm:text-xl">Planificar receta</CardTitle>
                <p className="text-sm text-gray-600 mt-1 line-clamp-2">
                  Selecciona el día y servicio para <span className="font-medium text-gray-900">{recipe.title}</span>
                </p>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="h-8 w-8 p-0 flex-shrink-0"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
          
          {/* Week Navigation - Responsive 2 líneas en móvil */}
          <div className="mt-4 space-y-3">
            {/* Fecha actual centrada */}
            <div className="text-center">
              <p className="font-semibold text-lg">
                {weekStart.toLocaleDateString('es-ES', { 
                  day: 'numeric', 
                  month: 'long',
                  year: 'numeric'
                })}
              </p>
              <p className="text-sm text-gray-500">
                Semana del {weekStart.toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })} al{' '}
                {new Date(weekStart.getTime() + 6 * 24 * 60 * 60 * 1000).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })}
              </p>
            </div>
            
            {/* Botones de navegación */}
            <div className="flex items-center justify-center gap-4">
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek('prev')}
                className="h-10 px-4 flex-1 max-w-[140px] touch-manipulation 
                           active:scale-95 transition-transform duration-150"
              >
                ← Anterior
              </Button>
              
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigateWeek('next')}
                className="h-10 px-4 flex-1 max-w-[140px] touch-manipulation 
                           active:scale-95 transition-transform duration-150"
              >
                Siguiente →
              </Button>
            </div>
          </div>
        </CardHeader>

        <CardContent className="p-0 overflow-y-auto max-h-[65vh] sm:max-h-[60vh]">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-64 px-4">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mb-3"></div>
              <div className="text-gray-500 text-sm">Cargando calendario...</div>
            </div>
          ) : (
            <WeeklyCalendar
              weekStart={weekStart}
              mealPlan={mealPlan || undefined}
              onSlotSelect={handleSlotSelect}
              onViewRecipe={() => {}} // No-op en el modal
              onToggleFavorite={() => {}} // No-op en el modal
              isFavorite={() => false} // No-op en el modal
              className="border-0 shadow-none"
            />
          )}
        </CardContent>

        {/* Footer mejorado para móvil */}
        <div className="p-3 sm:p-4 border-t bg-gradient-to-r from-gray-50 to-gray-100">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
            {/* Tip responsive */}
            <div className="flex items-center justify-center sm:justify-start">
              <Badge variant="outline" className="text-xs px-3 py-1.5 bg-white/80 backdrop-blur-sm">
                <span className="mr-1">💡</span>
                <span className="hidden sm:inline">Tip: Haz clic en un slot vacío para planificar la receta</span>
                <span className="sm:hidden">Toca un slot vacío para planificar</span>
              </Badge>
            </div>
            
            {/* Botones de acción */}
            <div className="flex items-center gap-2 w-full sm:w-auto">
              <Button 
                variant="outline" 
                size="sm" 
                onClick={onClose}
                className="flex-1 sm:flex-none min-w-[100px] h-10 touch-manipulation 
                           active:scale-95 transition-transform duration-150"
              >
                Cancelar
              </Button>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );

  return createPortal(modal, document.body);
}

export default MealPlannerModal;
